/**
 * One person in the project: who (an "Invited" pill until they first sign in),
 * their role (changeable by a manager who outranks them; a step down is
 * confirmed first), last seen, and remove. Your own row says "(you)" and its
 * remove is "Leave". The last active owner's role and removal stay disabled,
 * with the reason.
 */

import { useState } from "react";
import { LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/overlay";
import { Pill } from "@/components/ui/pill";
import { Select } from "@/components/ui/select";
import { errorText } from "@/components/ui/states";
import { useCurrentUser } from "@/features/auth/hooks";
import { useChangeMemberRole, useRemoveMember } from "@/features/projects/hooks";
import {
  demotes,
  outranks,
  ROLE_DESCRIPTION,
  ROLE_LABEL,
  roleOptions,
  rolesUpTo,
} from "@/features/projects/roles";
import type { Member, ProjectRole } from "@/features/projects/types";
import { formatRelative } from "@/lib/format";
import { useUiStore } from "@/stores/ui-store";
import { DeleteButton } from "./delete-button";
import { LeaveProjectDialog, ONLY_OWNER } from "./leave-project-button";

function lastSeen(m: Member): string {
  if (m.last_seen_at) return formatRelative(m.last_seen_at);
  return m.pending ? "Invited · hasn't signed in yet" : "Hasn't signed in yet";
}

function ConfirmRole({
  member: m,
  role,
  self,
  pending,
  onConfirm,
  onClose,
}: {
  member: Member;
  role: ProjectRole;
  self: boolean;
  pending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const who = self ? "yourself" : (m.name ?? m.email);
  return (
    <Dialog
      title={`Make ${who} ${ROLE_LABEL[role]}?`}
      onClose={onClose}
      width={460}
      onSubmit={() => !pending && onConfirm()}
      footer={
        <>
          <span className="flex-1" />
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={pending}>
            Change role
          </Button>
        </>
      }
    >
      <p className="text-[13px] leading-relaxed text-muted">
        {ROLE_LABEL[m.role]} → {ROLE_LABEL[role]}: {ROLE_DESCRIPTION[role]}.
        {self && " You can't undo this yourself; someone with a higher role has to."}
      </p>
    </Dialog>
  );
}

/** `myRole` null: a read-only row (the caller may not change members). */
export function MemberRow({
  member: m,
  myRole,
  onlyOwner = false,
}: {
  member: Member;
  myRole: ProjectRole | null;
  onlyOwner?: boolean;
}) {
  const self = useCurrentUser().data?.email === m.email;
  const [chosen, setChosen] = useState<ProjectRole | null>(null);
  const [leaving, setLeaving] = useState(false);
  const change = useChangeMemberRole();
  const remove = useRemoveMember();
  const notify = useUiStore((s) => s.notify);
  const manages = myRole !== null && outranks(myRole, m.role);
  const fail = (fallback: string) => (e: Error) => notify(errorText(e, fallback), "error");
  const apply = (role: ProjectRole) =>
    change.mutate(
      { email: m.email, role },
      {
        onSuccess: () => (notify(`${m.name ?? m.email} is now ${ROLE_LABEL[role]}.`), setChosen(null)),
        onError: fail("Could not change the role."),
      },
    );
  const pick = (r: string) => {
    const role = r as ProjectRole;
    if (role === m.role) return;
    if (self || demotes(m.role, role)) setChosen(role);
    else apply(role);
  };
  const del = () =>
    remove.mutate(m.email, {
      onSuccess: () => notify(`Removed ${m.name ?? m.email}.`),
      onError: fail("Could not remove them."),
    });
  return (
    <>
      <tr>
        <td>
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="flex items-center gap-2 text-sm font-semibold">
              {m.name ?? m.email}
              {self && <span className="font-normal text-muted">(you)</span>}
              {m.pending && <Pill>Invited</Pill>}
            </span>
            {m.name && <span className="text-xs text-muted">{m.email}</span>}
            {m.pending && <span className="text-xs text-faint">Added by {m.added_by}</span>}
          </div>
        </td>
        <td>
          {manages ? (
            <div className="flex flex-col gap-1">
              <Select
                label={`Role for ${m.email}`}
                className="w-40"
                buttonClassName="h-8"
                value={m.role}
                disabled={change.isPending || onlyOwner}
                onChange={pick}
                options={roleOptions(rolesUpTo(myRole))}
              />
              {onlyOwner && <span className="text-xs text-muted">{ONLY_OWNER}</span>}
            </div>
          ) : (
            ROLE_LABEL[m.role]
          )}
        </td>
        <td className="text-muted">{lastSeen(m)}</td>
        <td>
          <div className="flex justify-end">
            {self ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={onlyOwner}
                title={onlyOwner ? ONLY_OWNER : "Leave project"}
                onClick={() => setLeaving(true)}
              >
                <LogOut size={13} strokeWidth={1.8} aria-hidden />
                Leave
              </Button>
            ) : (
              manages && (
                <DeleteButton
                  verb="Remove"
                  what={m.email}
                  pending={remove.isPending}
                  onDelete={del}
                  disabledReason={onlyOwner ? ONLY_OWNER : null}
                />
              )
            )}
          </div>
        </td>
      </tr>
      {chosen && (
        <ConfirmRole
          member={m}
          role={chosen}
          self={self}
          pending={change.isPending}
          onConfirm={() => apply(chosen)}
          onClose={() => setChosen(null)}
        />
      )}
      {leaving && <LeaveProjectDialog onClose={() => setLeaving(false)} />}
    </>
  );
}
